// Implements: F-UIX-019
/**
 * A horizontal bar chart in plain SVG: one bar per entry, its length in proportion to the
 * largest value, its label and figure written beside it. No chart dependency (S12b-1.2):
 * the view draws one series, which a few rectangles say as well as a library would.
 *
 * Accessible (UIX.8): the chart is one image named by its title and described by a text
 * alternative that names every bar (its whole label: a long one is cut with an ellipsis
 * only in the drawing) and its figure, and it is keyboard reachable
 * (`tabIndex=0`) so a keyboard user lands on it and hears that description.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { PLATFORM_LOCALE } from "../../../types/locale";
import styles from "./PlatformTotals.module.css";

export interface ChartBar {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** The figure as the reader reads it. */
  readonly valueText: string;
  /** The sentence of the text alternative for this bar. */
  readonly description: string;
}

export interface TotalsBarChartProps {
  readonly title: string;
  readonly bars: readonly ChartBar[];
}

const WIDTH = 640;
const LABEL_WIDTH = 200;
const VALUE_WIDTH = 72;
const BAR_HEIGHT = 20;
const GAP = 8;
const PLOT_WIDTH = WIDTH - LABEL_WIDTH - VALUE_WIDTH;
/** How many characters of a label fit in `LABEL_WIDTH` at the chart's 12px font. */
export const LABEL_CHARACTERS = 26;

const GRAPHEMES = new Intl.Segmenter(PLATFORM_LOCALE, { granularity: "grapheme" });

/** A label cut to fit beside its bar, with an ellipsis; the description keeps it whole. */
export function fitLabel(label: string, characters: number = LABEL_CHARACTERS): string {
  const glyphs = Array.from(GRAPHEMES.segment(label), (part) => part.segment);
  if (glyphs.length <= characters) return label;
  return `${glyphs.slice(0, characters - 1).join("").trimEnd()}…`;
}

/** A bar's length: in proportion to the largest value, nothing when every value is zero. */
export function barLength(value: number, largest: number): number {
  if (largest <= 0 || value <= 0) return 0;
  return (value / largest) * PLOT_WIDTH;
}

export function TotalsBarChart({ title, bars }: TotalsBarChartProps): ReactElement {
  const ids = useId();
  const largest = Math.max(0, ...bars.map((bar) => bar.value));
  const height = bars.length * (BAR_HEIGHT + GAP) + GAP;
  return (
    <figure className={styles.figure}>
      {/* A focusable image (UIX.8): it has no control, and reaching it by keyboard is how its description is heard. */}
      <svg
        className={styles.chart}
        role="img"
        tabIndex={0}
        aria-labelledby={`${ids}-title`}
        aria-describedby={`${ids}-desc`}
        viewBox={`0 0 ${String(WIDTH)} ${String(height)}`}
        preserveAspectRatio="xMinYMin meet"
      >
        <title id={`${ids}-title`}>{title}</title>
        {bars.map((bar, index) => {
          const y = GAP + index * (BAR_HEIGHT + GAP);
          const length = barLength(bar.value, largest);
          return (
            <g key={bar.key} aria-hidden="true">
              <text className={styles.chartLabel} data-label="" x={0} y={y + BAR_HEIGHT * 0.75}>
                {fitLabel(bar.label)}
              </text>
              <rect
                className={styles.bar}
                data-bar=""
                data-value={String(bar.value)}
                x={LABEL_WIDTH}
                y={y}
                width={length}
                height={BAR_HEIGHT}
              />
              <text className={styles.chartLabel} x={LABEL_WIDTH + length + 6} y={y + BAR_HEIGHT * 0.75}>
                {bar.valueText}
              </text>
            </g>
          );
        })}
      </svg>
      <p id={`${ids}-desc`} className={styles.visuallyHidden}>
        {bars.map((bar) => bar.description).join(" ")}
      </p>
    </figure>
  );
}
